import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MediaUploadContext,
  registerMediaUpload,
  requestSignedMediaUpload,
  uploadMediaToSignedUrl,
} from "#/api/mediaReference.api";
import { useToast } from "#/hooks/ToastContext";

type UploadPayload = {
  file: File;
  context: MediaUploadContext;
};

type UploadResult = {
  id: string;
  key: string;
  cdn_url: string;
};

async function getImageDimensions(file: File) {
  return new Promise<{ width?: number; height?: number }>((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      resolve({ width: image.width, height: image.height });
      URL.revokeObjectURL(url);
    };

    image.onerror = () => {
      resolve({});
      URL.revokeObjectURL(url);
    };

    image.src = url;
  });
}

export function useUploadMediaReference() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  return useMutation<UploadResult, Error, UploadPayload>({
    mutationFn: async ({ file, context }) => {
      const signed = await requestSignedMediaUpload({
        contentType: file.type || "application/octet-stream",
        size: file.size,
        fileName: file.name,
        context,
        method: "post",
      });

      await uploadMediaToSignedUrl(file, signed);

      const metadata = await getImageDimensions(file);
      const registered = await registerMediaUpload({
        key: signed.key,
        context,
        source: "s3",
        client_metadata: metadata,
      });

      return {
        id: registered.id,
        key: registered.key,
        cdn_url: registered.cdn_url,
      };
    },
    onSuccess: () => {
      showToast("Media uploaded", "success");
      queryClient.invalidateQueries({ queryKey: ["media", "search"] });
    },
    onError: (error) => {
      showToast(
        error instanceof Error ? error.message : "Upload failed",
        "error"
      );
    },
  });
}
